// src/hooks/useNavbarHeight.ts

'use client'

import { useState, useEffect, useCallback } from 'react'

/**
 * CSS selectors for navbar elements that contribute to total header height.
 * @constant
 */
const NAVBAR_SELECTORS = {
  /** Main site header element */
  MAIN_HEADER: 'header',
  /** Sticky navigation element */
  STICKY_NAV: 'nav.sticky',
  /** Fixed header element */
  FIXED_HEADER: 'header.fixed',
  /** Blog-specific navigation */
  BLOG_NAV: '.blog-nav',
  /** Blog header navigation (nested selector) */
  BLOG_HEADER_NAV: 'header.fixed nav'
} as const

/** Default navbar height used as fallback (in pixels) */
const DEFAULT_NAVBAR_HEIGHT = 240

/** Minimum navbar height to prevent layout issues (in pixels) */
const MIN_NAVBAR_HEIGHT = 200

/**
 * Calculates and tracks the total height of all fixed/sticky navigation elements.
 * 
 * This hook dynamically measures navbar heights across the page, including:
 * - Main site header
 * - Blog-specific headers
 * - Sticky/fixed navigation elements
 * 
 * The hook uses ResizeObserver for efficient DOM measurements and requestAnimationFrame
 * to batch updates, minimizing layout thrashing and ensuring optimal performance.
 * 
 * @returns {number} The total combined height of all navbar elements in pixels.
 *                   Returns a minimum of MIN_NAVBAR_HEIGHT to prevent layout issues.
 * 
 * @example
 * ```tsx
 * function ScrollToTopButton() {
 *   const navbarHeight = useNavbarHeight()
 *   
 *   const scrollToTop = () => {
 *     window.scrollTo({ top: navbarHeight, behavior: 'smooth' })
 *   }
 *   
 *   return <button onClick={scrollToTop}>Back to Top</button>
 * }
 * ```
 */
export function useNavbarHeight(): number {
  const [totalNavbarHeight, setTotalNavbarHeight] = useState<number>(DEFAULT_NAVBAR_HEIGHT)

  /**
   * Calculates the total height of all navbar elements by querying the DOM.
   * Falls back to safe defaults if elements are not found or errors occur.
   */
  const calculateTotalNavbarHeight = useCallback((): void => {
    /**
     * Gets the height of an element matching the given selector.
     * @param {string} selector - CSS selector for the target element
     * @returns {number} Height in pixels, or 0 if element not found
     */
    const getElementHeight = (selector: string): number => {
      const element = document.querySelector<HTMLElement>(selector)
      return element?.getBoundingClientRect().height ?? 0
    }

    try {
      // Calculate main navbar height (try multiple selectors)
      const mainNavbarHeight =
        getElementHeight(NAVBAR_SELECTORS.MAIN_HEADER) ||
        getElementHeight(NAVBAR_SELECTORS.STICKY_NAV)

      // Calculate blog-specific header heights
      const blogHeaderHeight = getElementHeight(NAVBAR_SELECTORS.FIXED_HEADER)
      const blogNavHeight =
        getElementHeight(NAVBAR_SELECTORS.BLOG_NAV) ||
        getElementHeight(NAVBAR_SELECTORS.BLOG_HEADER_NAV)

      // Sum all navbar heights
      const calculatedHeight = mainNavbarHeight + blogHeaderHeight + blogNavHeight

      // Use bottom position of fixed header as additional fallback
      const fallbackHeight =
        document.querySelector<HTMLElement>(NAVBAR_SELECTORS.FIXED_HEADER)?.getBoundingClientRect().bottom ??
        MIN_NAVBAR_HEIGHT

      // Ensure we never return a height below the minimum
      const totalHeight = Math.max(calculatedHeight, fallbackHeight, MIN_NAVBAR_HEIGHT)
      setTotalNavbarHeight(totalHeight)
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.warn('useNavbarHeight: Error calculating navbar height', error)
      }
      setTotalNavbarHeight(DEFAULT_NAVBAR_HEIGHT)
    }
  }, [])

  useEffect(() => {
    // Skip setup in SSR environment
    if (typeof window === 'undefined') return

    let frameId: number | null = null
    const observedElements = new Set<Element>()

    /**
     * Finds and observes all navbar elements for size changes.
     * Prevents duplicate observations using a Set.
     */
    const observeRelevantElements = (): void => {
      if (typeof document === 'undefined') return

      Object.values(NAVBAR_SELECTORS).forEach((selector) => {
        document.querySelectorAll(selector).forEach((element) => {
          if (!observedElements.has(element)) {
            resizeObserver.observe(element)
            observedElements.add(element)
          }
        })
      })
    }

    /**
     * Schedules a navbar height measurement using requestAnimationFrame.
     * Batches multiple calls to prevent excessive recalculations.
     */
    const scheduleMeasurement = (): void => {
      observeRelevantElements()

      // Prevent duplicate scheduled measurements
      if (frameId !== null) return

      frameId = window.requestAnimationFrame(() => {
        frameId = null
        calculateTotalNavbarHeight()
      })
    }

    // Create ResizeObserver to watch for navbar size changes
    const resizeObserver = new ResizeObserver(() => scheduleMeasurement())

    /**
     * Handles window resize and orientation change events.
     */
    const handleResize = (): void => {
      scheduleMeasurement()
    }

    // Initial setup
    observeRelevantElements()
    scheduleMeasurement()

    // Listen for layout changes
    window.addEventListener('resize', handleResize)
    window.addEventListener('orientationchange', handleResize)
    window.addEventListener('load', handleResize, { once: true })

    // Delayed measurement to catch late-loading elements
    const initialTimer = window.setTimeout(handleResize, 500)

    // Cleanup function
    return () => {
      if (frameId !== null) {
        window.cancelAnimationFrame(frameId)
      }

      window.clearTimeout(initialTimer)
      window.removeEventListener('resize', handleResize)
      window.removeEventListener('orientationchange', handleResize)
      resizeObserver.disconnect()
    }
  }, [calculateTotalNavbarHeight])

  return totalNavbarHeight
}